import React from 'react';
import {
  TextInput, NumberInput, BooleanInput, required, useNotify, useRecordContext, useTranslate,
} from 'react-admin';
import { IconButton, InputAdornment, Tooltip, useTheme } from '@mui/material';
import { ContentCopyRounded as CopyIcon } from '@mui/icons-material';
import { FieldPair, FormSection } from '@/components/shell';

const CopyApiKeyButton: React.FC = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const t = useTranslate();
  const label = t('screens.servers.copyKey');

  const handleCopy = () => {
    const key = record?.apiKey;
    if (key) {
      navigator.clipboard.writeText(key);
      notify('screens.servers.keyCopied');
    }
  };

  return (
    <Tooltip title={label}>
      <IconButton onClick={handleCopy} aria-label={label} edge="end">
        <CopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

/** The fields of a server, shared by its create and edit pages. */
export const ServerFields: React.FC<{ mode: 'create' | 'edit' }> = ({ mode }) => {
  const t = useTranslate();
  const theme = useTheme();

  return (
    <>
      <FormSection title={t('screens.servers.sectionConnection')}>
        <FieldPair>
          <TextInput source="name" label={t('resources.servers.fields.name')} validate={required()} fullWidth />
          <NumberInput source="port" label={t('resources.servers.fields.port')} validate={required()} fullWidth />
        </FieldPair>
        <TextInput
          source="url"
          label={t('resources.servers.fields.url')}
          validate={required()}
          fullWidth
          helperText={mode === 'create' ? t('screens.servers.urlHint') : undefined}
        />
        <BooleanInput source="isForcedOffline" label={t('resources.servers.fields.isForcedOffline')} />
      </FormSection>

      <FormSection title={t('screens.servers.sectionAccess')}>
        <TextInput
          source="apiKey"
          label={t('resources.servers.fields.apiKey')}
          validate={mode === 'create' ? required() : undefined}
          fullWidth
          InputProps={{
            sx: { fontFamily: theme.custom.text.mono },
            endAdornment:
              mode === 'edit' ? (
                <InputAdornment position="end">
                  <CopyApiKeyButton />
                </InputAdornment>
              ) : undefined,
          }}
        />
      </FormSection>
    </>
  );
};
